import React, {
  useMemo,
  useState,
  useCallback,
  PropsWithChildren,
} from "react";
import { cx, useAppVariant } from "@jugl-web/utils";

// utils
import { getSupportedPlatForm } from "@web-src/utils/device";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";

// themes
import { ArgumentArray } from "classnames";
import { APP_THEME_KEY } from "@jugl-web/utils/storage";
import {
  CustomThemeContext,
  CustomThemeContextValue,
  ThemeMode,
} from "./CustomThemeContext";

const CustomThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [device, setDevice] = useState(() => getSupportedPlatForm());
  const { isCx } = useAppVariant();
  const [customTheme, setCustomTheme] = useLocalStorage<
    CustomThemeContextValue["customTheme"]
  >(APP_THEME_KEY, "blue");

  const setDeviceHandler = (type: string) => {
    setDevice(type);
  };

  // Example usage (remove later):
  // tcx("bg-gray-500 ...", {
  //   blue: "text-blue-500",
  //   dark: "text-dark-500",
  //   light: "text-light-500",
  // }, ...);
  const tcx: CustomThemeContextValue["tcx"] = useCallback(
    (...args) =>
      cx(
        args.map((item) => {
          if (
            item &&
            typeof item === "object" &&
            ("blue" in item || "dark" in item || "light" in item)
          ) {
            const themeToUse = isCx ? "dark" : customTheme;
            return cx((item as Record<ThemeMode, ArgumentArray>)[themeToUse]);
          }
          return item;
        })
      ),
    [customTheme, isCx]
  );

  const contextValue = useMemo(
    () => ({
      tcx,
      customTheme,
      setCustomTheme,
      darkMode: false,
      device,
      setDevice: setDeviceHandler,
    }),
    [customTheme, device, setCustomTheme, tcx]
  );

  return (
    <CustomThemeContext.Provider value={contextValue}>
      {children}
    </CustomThemeContext.Provider>
  );
};

export { CustomThemeProvider };
